import {
  GitLabGroup,
  GitlabWebhookInstallResult,
} from 'network/httpClients/GitlabClient';

/**
 * Merges two webhook install results into one combined result, deduplicating by projectId.
 * A project that succeeded in either result is counted as succeeded once.
 * A project is only counted as failed if it did not succeed in either result.
 * @param {GitlabWebhookInstallResult} a - The first webhook install result
 * @param {GitlabWebhookInstallResult} b - The second webhook install result
 * @returns {GitlabWebhookInstallResult} A merged result with deduplicated succeeded and failed entries
 */
export const mergeWebhookResults = (
  a: GitlabWebhookInstallResult,
  b: GitlabWebhookInstallResult,
): GitlabWebhookInstallResult => {
  const succeeded = new Map(
    [...a.succeeded, ...b.succeeded].map((r) => [r.projectId, r]),
  );
  const failed = new Map(
    [...a.failed, ...b.failed]
      .filter((r) => !succeeded.has(r.projectId))
      .map((r) => [r.projectId, r]),
  );

  return {
    succeeded: Array.from(succeeded.values()),
    failed: Array.from(failed.values()),
  };
};

/**
 * Returns a new Set with the item added if absent, or removed if present.
 * @param {Set<T>} set - The original set (not mutated)
 * @param {T} item - The item to toggle
 * @returns {Set<T>} A new set with the item toggled
 */
export const toggleInSet = <T>(set: Set<T>, item: T): Set<T> => {
  const newSet = new Set(set);

  if (newSet.has(item)) {
    newSet.delete(item);
  } else {
    newSet.add(item);
  }

  return newSet;
};

/**
 * The root group is the connected group that contains the others — the entry
 * with the fewest fullPath segments. The /groups list is `[root, ...children]`.
 * @param {GitLabGroup[]} groups - The full group list (root + subgroups).
 * @returns {GitLabGroup | undefined} The root group, or undefined for an empty list.
 */
export const getRootGroup = (
  groups: GitLabGroup[],
): GitLabGroup | undefined => {
  if (groups.length === 0) return undefined;

  return groups.reduce((root, group) =>
    group.fullPath.split('/').length < root.fullPath.split('/').length
      ? group
      : root,
  );
};

/**
 * Depth of a group relative to the root: 0 for the root, 1 for a direct child.
 * Only groups nested under the root are indented — a group from a different tree
 * (not a descendant of the root) returns 0 so it isn't mis-indented beneath it.
 * @param {GitLabGroup} group - The group to measure.
 * @param {GitLabGroup} rootGroup - The root the depth is relative to.
 * @returns {number} 0 for the root or any non-descendant, else its nesting depth.
 */
export const getGroupDepth = (
  group: GitLabGroup,
  rootGroup: GitLabGroup,
): number => {
  if (!isDescendantOf(group, rootGroup)) return 0;

  const rootSegments = rootGroup.fullPath.split('/').length;
  const groupSegments = group.fullPath.split('/').length;

  return groupSegments - rootSegments;
};

/**
 * Whether `group` is nested under `ancestor` (a strict descendant), matched on
 * the fullPath segment boundary so e.g. `acme-2` is not treated as under `acme`.
 * @param {GitLabGroup} group - The group to test.
 * @param {GitLabGroup} ancestor - The candidate ancestor group.
 * @returns {boolean} True when `group` is strictly nested under `ancestor`.
 */
export const isDescendantOf = (
  group: GitLabGroup,
  ancestor: GitLabGroup,
): boolean => group.fullPath.startsWith(`${ancestor.fullPath}/`);

/**
 * Group ids to send to install-by-groups. Selecting the root installs its whole
 * subtree (include_subgroups), so any selected group nested under the selected
 * root is dropped (the root already covers it). Selected groups outside the
 * root's subtree are preserved rather than silently collapsed.
 * @param {Set<number>} selectedGroupIds - Ids of all whole-group selections.
 * @param {GitLabGroup[]} groups - The full group list (root + subgroups).
 * @returns {number[]} Group ids to install, with root-covered descendants removed.
 */
export const getEffectiveGroupIds = (
  selectedGroupIds: Set<number>,
  groups: GitLabGroup[],
): number[] => {
  const rootGroup = getRootGroup(groups);

  if (!rootGroup || !selectedGroupIds.has(rootGroup.id)) {
    return Array.from(selectedGroupIds);
  }

  const groupsById = new Map(groups.map((group) => [group.id, group]));

  return Array.from(selectedGroupIds).filter((id) => {
    const group = groupsById.get(id);

    return !group || !isDescendantOf(group, rootGroup);
  });
};
